// Xero account/tax-rate mappings editor (BUILD_SPEC.md §5.9, §12) —
// services/api/src/modules/xero/mappings.ts. `kind`+`key` identify a row and
// are immutable after creation (delete + recreate to change them); only the
// Xero-side value is ever updated in place — this editor's "Edit" form
// disables both fields to match. ADMIN only (`requireRole('ADMIN')` on every
// mapping route, including reads).
import { useState } from 'react';
import { Alert, Button, Group, Modal, Paper, Select, Stack, Table, Text, TextInput, Title, Tooltip } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertTriangle, IconPlus, IconTrash } from '@tabler/icons-react';
import { VAT_TREATMENTS, XERO_MAPPING_KINDS, type XeroMappingCreate, type XeroMappingResponse } from '@zebra/core';
import { ApiRequestError } from '../../../api/client';
import { ConfirmButton } from '../../../components/ConfirmButton';
import { useIsAdmin } from '../permissions';
import { useCreateXeroMapping, useDeleteXeroMapping, useUpdateXeroMapping, useXeroMappingValidation, useXeroMappings } from '../api';

const KEY_HINTS: Record<string, string> = {
  ACCOUNT: 'e.g. DEFAULT_SALES_ACCOUNT, DEFAULT_PURCHASE_ACCOUNT',
  TAX_RATE: `e.g. ${VAT_TREATMENTS.join(', ')}`,
};

function MappingFormModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const create = useCreateXeroMapping();
  const form = useForm<XeroMappingCreate>({
    initialValues: { kind: 'ACCOUNT', key: '', xeroValue: '' },
    validate: {
      key: (v) => (v.trim() ? null : 'Key is required'),
      xeroValue: (v) => (v.trim() ? null : 'Xero value is required'),
    },
  });

  function handleSubmit(values: XeroMappingCreate) {
    create.mutate(
      { kind: values.kind, key: values.key.trim(), xeroValue: values.xeroValue.trim() },
      { onSuccess: () => { onClose(); form.reset(); } },
    );
  }

  return (
    <Modal opened={opened} onClose={onClose} title="New Xero mapping" centered>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="sm">
          {create.isError && (
            <Alert color="red" icon={<IconAlertTriangle size={16} />}>
              {create.error instanceof ApiRequestError ? create.error.message : 'Failed to create mapping.'}
            </Alert>
          )}
          <Select label="Kind" data={[...XERO_MAPPING_KINDS]} required {...form.getInputProps('kind')} />
          <TextInput label="Key" description={KEY_HINTS[form.values.kind]} required {...form.getInputProps('key')} />
          <TextInput label="Xero value" description="The Xero chart-of-accounts code or tax type" required {...form.getInputProps('xeroValue')} />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={create.isPending}>
              Create mapping
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

function MappingRow({ row }: { row: XeroMappingResponse }) {
  const [editing, setEditing] = useState(false);
  const [xeroValue, setXeroValue] = useState(row.xeroValue);
  const update = useUpdateXeroMapping(row.id);
  const remove = useDeleteXeroMapping(row.id);

  function save() {
    update.mutate({ xeroValue: xeroValue.trim() }, { onSuccess: () => setEditing(false) });
  }

  return (
    <Table.Tr>
      <Table.Td>{row.kind}</Table.Td>
      <Table.Td>{row.key}</Table.Td>
      <Table.Td>
        {editing ? (
          <TextInput value={xeroValue} onChange={(e) => setXeroValue(e.currentTarget.value)} size="xs" />
        ) : (
          row.xeroValue
        )}
      </Table.Td>
      <Table.Td>
        <Group gap="xs" wrap="nowrap">
          {editing ? (
            <>
              <Button size="xs" variant="light" onClick={save} loading={update.isPending}>
                Save
              </Button>
              <Button size="xs" variant="default" onClick={() => { setEditing(false); setXeroValue(row.xeroValue); }}>
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button size="xs" variant="subtle" onClick={() => setEditing(true)}>
                Edit
              </Button>
              <Tooltip label="Deletes this mapping — Xero sync for documents that need it will fail until it's recreated.">
                <span>
                  <ConfirmButton
                    size="xs"
                    variant="subtle"
                    color="red"
                    leftSection={<IconTrash size={14} />}
                    loading={remove.isPending}
                    label="Delete"
                    message={`Delete the ${row.kind} mapping for "${row.key}"? Any document that needs it for Xero sync will fail validation until it's recreated.`}
                    onConfirm={() => remove.mutate()}
                  />
                </span>
              </Tooltip>
            </>
          )}
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}

export function XeroMappingsEditor() {
  const isAdmin = useIsAdmin();
  const mappingsQuery = useXeroMappings();
  const validationQuery = useXeroMappingValidation();
  const [modalOpened, setModalOpened] = useState(false);
  const rows = mappingsQuery.data?.items ?? [];

  if (!isAdmin) {
    return (
      <Paper withBorder radius="md" p="lg">
        <Text size="sm" c="dimmed">
          ADMIN role is required to view or edit Xero account/tax-rate mappings.
        </Text>
      </Paper>
    );
  }

  const validation = validationQuery.data;

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={3}>Account &amp; tax-rate mappings</Title>
        <Button leftSection={<IconPlus size={16} />} size="sm" onClick={() => setModalOpened(true)}>
          New mapping
        </Button>
      </Group>
      <Text size="sm" c="dimmed">
        Tells Xero which of your accounts and tax rates each document line should use — pushing a document that needs a
        missing mapping fails until it's added here.
      </Text>

      {validation && !validation.valid && (
        <Alert color="amber" icon={<IconAlertTriangle size={16} />} title="Missing mappings">
          {validation.missingAccountKeys.length > 0 && <div>ACCOUNT: {validation.missingAccountKeys.join(', ')}</div>}
          {validation.missingTaxRateKeys.length > 0 && <div>TAX_RATE: {validation.missingTaxRateKeys.join(', ')}</div>}
        </Alert>
      )}
      {validation?.valid && (
        <Alert color="green" variant="light">
          All required account and tax-rate mappings are present.
        </Alert>
      )}

      <Paper withBorder radius="md">
        <Table.ScrollContainer minWidth={520}>
          <Table striped highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Kind</Table.Th>
                <Table.Th>Key</Table.Th>
                <Table.Th>Xero value</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <MappingRow key={row.id} row={row} />
              ))}
              {!mappingsQuery.isLoading && rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text c="dimmed" ta="center" py="md">
                      No mappings yet.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>

      <MappingFormModal opened={modalOpened} onClose={() => setModalOpened(false)} />
    </Stack>
  );
}
